import React, { useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import SeriesReportCard from './SeriesReportCard';
import useExportTactic from './useExportTactic';
import css from './BOArchivePanel.module.scss';

// BO 档案抽屉：左侧列表，右侧详情（预览 + 操作）
// - 列表：每行 = 一个 BO（label / 比分 / 时间 / 模式 / 局数）
// - 详情：选中后看到完整 BO 摘要 + 重命名 + 改胜负 + 加载到主界面 + 导出 PNG + 删除
// - 顶部：导入 / 导出全部 / 关闭
//
// "导出系列总结 PNG" 用离屏的 SeriesReportCard 渲染后用 html2canvas 截图。
// 这个 ref 必须随选中的 archive 变化而切换 → 我们用 useExportTactic 的 getCaptureNode
// 返回 ref.current 即可，组件每次切换 archive 都会重渲。

const formatWhen = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatExportedAt = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const BOArchivePanel = ({
  open,
  onClose,
  archives,
  onRename,           // (id, { label?, blueName?, redName? }) => void
  onRemove,           // (id) => void
  onSetGameWinner,    // (id, gameIndex, winner) => void
  onDownloadJson,     // (archive) => void
  onDownloadAll,      // () => void
  onImportFile,       // (file) => Promise<{ok, count, msg}>
  onLoadToMain,       // (archive) => void  ← 把存档加载到主界面（破坏性）
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [importMsg, setImportMsg] = useState('');
  const reportRef = useRef(null);
  const fileInputRef = useRef(null);

  // 自动选中第一个（如果还没选）
  const selected = useMemo(() => {
    if (!archives.length) return null;
    if (selectedId) {
      const found = archives.find((a) => a.id === selectedId);
      if (found) return found;
    }
    return archives[0];
  }, [archives, selectedId]);

  const exportPng = useExportTactic({
    getCaptureNode: () => reportRef.current,
    filenamePrefix: selected ? `Series-${(selected.label || selected.id).replace(/[^\w\u4e00-\u9fa5-]/g, '_')}` : 'Series',
  });

  if (!open) {
    return (
      <>
        {/* 离屏渲染 SeriesReportCard，即使关闭也保留 ref 以便快速导出 */}
        <div className={css.OffscreenStage} aria-hidden="true">
          <SeriesReportCard ref={reportRef} archive={selected} exportedAtFormatted={formatExportedAt()} />
        </div>
      </>
    );
  }

  const handleImportClick = () => {
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleImportChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const res = await onImportFile(file);
    if (res.ok) {
      setImportMsg(`✓ 导入成功，合并 ${res.count} 个存档`);
    } else {
      setImportMsg(`✗ 导入失败：${res.msg || ''}`);
    }
    e.target.value = ''; // 清空，下次同名文件也能再选
    setTimeout(() => setImportMsg(''), 3500);
  };

  return (
    <>
      <div className={css.Mask} onClick={onClose} />
      <div className={css.Drawer} role="dialog" aria-label="BO 档案">
        {/* 顶部 */}
        <div className={css.Header}>
          <span className={css.HeaderIcon} aria-hidden="true">📁</span>
          <span className={css.HeaderTitle}>BO 档案</span>
          <span className={css.HeaderCount}>共 {archives.length} 条</span>

          <div className={css.HeaderActions}>
            <button type="button" className={css.HBtn} onClick={handleImportClick} title="从 JSON 文件导入">
              导入
            </button>
            <button type="button" className={css.HBtn} onClick={onDownloadAll} title="把全部存档打包下载为 JSON">
              备份全部
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleImportChange}
            />
            <button type="button" className={css.HBtnClose} onClick={onClose} aria-label="关闭">✕</button>
          </div>
        </div>

        {importMsg && <div className={css.ImportMsg}>{importMsg}</div>}

        {/* 主体：左列表 + 右详情 */}
        <div className={css.Body}>
          {/* 左列表 */}
          <div className={css.List}>
            {archives.length === 0 && (
              <div className={css.EmptyHint}>
                <div className={css.EmptyTitle}>暂无 BO 档案</div>
                <div className={css.EmptyTip}>
                  在 BO 控制台点【保存当前 BO】，<br />
                  或导入 JSON 文件即可看到这里。
                </div>
              </div>
            )}
            {archives.map((a) => (
              <button
                key={a.id}
                type="button"
                className={cx(css.ListItem, selected && selected.id === a.id && css.ListItemActive)}
                onClick={() => setSelectedId(a.id)}
              >
                <div className={css.ItemTop}>
                  <span className={css.ItemMode}>{a.mode}</span>
                  <span className={css.ItemTime}>{formatWhen(a.savedAt)}</span>
                </div>
                <div className={css.ItemLabel}>{a.label}</div>
                <div className={css.ItemScore}>
                  <span className={css.ItemTeamBlue}>{a.blueName || '蓝'}</span>
                  <strong className={css.ItemScoreNum}>
                    <span className={css.ItemScoreBlue}>{a.scores?.blue ?? 0}</span>
                    <span className={css.ItemScoreDash}>:</span>
                    <span className={css.ItemScoreRed}>{a.scores?.red ?? 0}</span>
                  </strong>
                  <span className={css.ItemTeamRed}>{a.redName || '红'}</span>
                </div>
                <div className={css.ItemMeta}>
                  {a.games?.length || 0}/{a.totalGames} 局
                  {a.fearlessEnabled && <span className={css.ItemFearless}>Fearless</span>}
                </div>
              </button>
            ))}
          </div>

          {/* 右详情 */}
          <div className={css.Detail}>
            {!selected ? (
              <div className={css.DetailEmpty}>← 从左侧选一个 BO</div>
            ) : (
              <DetailPane
                archive={selected}
                onRename={onRename}
                onRemove={(id) => {
                  if (window.confirm(`确定要删除「${selected.label}」吗？\n此操作无法撤销。`)) {
                    onRemove(id);
                    setSelectedId(null);
                  }
                }}
                onSetGameWinner={onSetGameWinner}
                onDownloadJson={onDownloadJson}
                onLoadToMain={(arc) => {
                  if (window.confirm(
                    `加载「${arc.label}」会覆盖当前正在打的 BO（当前进度会丢失）。\n确认加载？`
                  )) {
                    onLoadToMain(arc);
                    onClose();
                  }
                }}
                exportPng={exportPng}
              />
            )}
          </div>
        </div>

        {/* 离屏舞台：SeriesReportCard 渲染目标 */}
        <div className={css.OffscreenStage} aria-hidden="true">
          <SeriesReportCard
            ref={reportRef}
            archive={selected}
            exportedAtFormatted={formatExportedAt()}
          />
        </div>
      </div>
    </>
  );
};

// ============ Detail Pane ============

const DetailPane = ({
  archive, onRename, onRemove, onSetGameWinner,
  onDownloadJson, onLoadToMain, exportPng,
}) => {
  const [editLabel, setEditLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(archive.label);
  const [blueDraft, setBlueDraft] = useState(archive.blueName || '');
  const [redDraft, setRedDraft] = useState(archive.redName || '');

  // 切换 archive 时重置草稿
  React.useEffect(() => {
    setLabelDraft(archive.label);
    setBlueDraft(archive.blueName || '');
    setRedDraft(archive.redName || '');
    setEditLabel(false);
  }, [archive.id, archive.label, archive.blueName, archive.redName]);

  const commitLabel = () => {
    onRename(archive.id, { label: labelDraft || archive.label });
    setEditLabel(false);
  };

  const commitTeams = () => {
    onRename(archive.id, {
      blueName: blueDraft || '蓝方',
      redName: redDraft || '红方',
    });
  };

  return (
    <div className={css.DetailInner}>
      {/* 顶部信息条 */}
      <div className={css.DetailHeader}>
        {editLabel ? (
          <input
            className={css.LabelInput}
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); }}
            autoFocus
          />
        ) : (
          <button
            type="button"
            className={css.LabelDisplay}
            onClick={() => setEditLabel(true)}
            title="点击重命名"
          >
            {archive.label}
            <span className={css.LabelEditHint}>✎</span>
          </button>
        )}
        <span className={css.DetailWhen}>{formatWhen(archive.savedAt)}</span>
      </div>

      {/* 队名 + 比分 */}
      <div className={css.TeamsRow}>
        <div className={css.TeamCard_blue}>
          <input
            className={css.TeamNameInput}
            value={blueDraft}
            onChange={(e) => setBlueDraft(e.target.value)}
            onBlur={commitTeams}
            placeholder="蓝方"
            maxLength={16}
          />
          <span className={css.TeamScoreBlue}>{archive.scores?.blue ?? 0}</span>
        </div>
        <span className={css.TeamsVs}>vs</span>
        <div className={css.TeamCard_red}>
          <span className={css.TeamScoreRed}>{archive.scores?.red ?? 0}</span>
          <input
            className={css.TeamNameInput}
            value={redDraft}
            onChange={(e) => setRedDraft(e.target.value)}
            onBlur={commitTeams}
            placeholder="红方"
            maxLength={16}
          />
        </div>
      </div>

      {/* 每局：胜负标记 */}
      <div className={css.GamesSection}>
        <div className={css.SectionTitle}>每局胜负</div>
        <div className={css.GamesGrid}>
          {archive.games.map((g) => (
            <div key={g.gameIndex} className={css.GameWinnerRow}>
              <span className={css.GameWinnerLabel}>G{g.gameIndex + 1}</span>
              <span className={cx(css.GameWinnerFirst, css[`GameWinnerFirst_${g.firstSide}`])}>
                {g.firstSide === 'blue' ? '蓝先' : '红先'}
              </span>
              <div className={css.GameWinnerBtns}>
                <button
                  type="button"
                  className={cx(css.WinnerBtn_blue, g.winner === 'blue' && css.WinnerBtnActive)}
                  onClick={() => onSetGameWinner(archive.id, g.gameIndex, g.winner === 'blue' ? null : 'blue')}
                  title="标记蓝方胜"
                >
                  蓝胜
                </button>
                <button
                  type="button"
                  className={cx(css.WinnerBtn_red, g.winner === 'red' && css.WinnerBtnActive)}
                  onClick={() => onSetGameWinner(archive.id, g.gameIndex, g.winner === 'red' ? null : 'red')}
                  title="标记红方胜"
                >
                  红胜
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 操作按钮组 */}
      <div className={css.ActionsBar}>
        <button
          type="button"
          className={css.PrimaryBtn}
          onClick={exportPng.exportNow}
          disabled={exportPng.exporting}
          title="把当前选中的 BO 渲染成一张 1920×1080 系列总结图"
        >
          {exportPng.exporting ? '导出中…' : '导出系列总结 PNG'}
        </button>
        <button
          type="button"
          className={css.SecondaryBtn}
          onClick={() => onDownloadJson(archive)}
        >
          下载 JSON
        </button>
        <button
          type="button"
          className={css.SecondaryBtn}
          onClick={() => onLoadToMain(archive)}
          title="把这个 BO 加载回主界面（破坏性，会覆盖当前 BO）"
        >
          加载到主界面
        </button>
        <button
          type="button"
          className={css.DangerBtn}
          onClick={() => onRemove(archive.id)}
        >
          删除
        </button>
      </div>
    </div>
  );
};

export default BOArchivePanel;
